import React, { useState, useEffect } from 'react';
import { apiFetch } from '../services/api_service';
import { API } from '../App';
import { AlertTriangle, Shield, Clock, CheckCircle } from 'lucide-react';

const RiskManagement = () => {
  const [risks, setRisks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterCategory, setFilterCategory] = useState('ALL');
  const [filterLevel, setFilterLevel] = useState('ALL');

  const fetchRisks = async () => {
    const data = await apiFetch(`${API}/risks`);
    if (data?._error) {
      console.error('Error fetching risks:', data.message);
      setLoading(false);
      return;
    }
    setRisks(data);
    setLoading(false);
  };

   
  useEffect(() => {
    fetchRisks();
  }, []);

  const riskLevelColors = {
    critical: { bg: 'bg-red-100', text: 'text-red-800', border: 'border-red-600' },
    high: { bg: 'bg-orange-100', text: 'text-orange-800', border: 'border-orange-600' },
    medium: { bg: 'bg-yellow-100', text: 'text-yellow-800', border: 'border-yellow-600' },
    low: { bg: 'bg-green-100', text: 'text-green-800', border: 'border-green-600' }
  };

  const statusIcons = {
    open: <AlertTriangle className="text-red-600" size={20} />,
    mitigating: <Shield className="text-orange-600" size={20} />,
    resolved: <CheckCircle className="text-green-600" size={20} />
  };

  if (loading) {
    return <div className="flex items-center justify-center h-96"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div></div>;
  }

  const filteredRisks = risks.filter(risk => {
    const categoryMatch = filterCategory === 'ALL' || risk.category === filterCategory;
    const levelMatch = filterLevel === 'ALL' || risk.risk_level === filterLevel;
    return categoryMatch && levelMatch;
  });

  return (
    <div className="space-y-6" data-testid="risks-page">
      <div>
        <h1 className="text-3xl font-bold text-slate-900 mb-2">Risk Management</h1>
        <p className="text-slate-600">Migration and compliance risk tracking</p>
      </div>

      {/* Risk Summary */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-red-50 border-l-4 border-red-600 rounded-lg p-4">
          <div className="text-2xl font-bold text-red-900">{risks.filter(r => r.risk_level === 'critical').length}</div>
          <div className="text-sm text-red-700">Critical Risks</div>
        </div>
        <div className="bg-orange-50 border-l-4 border-orange-600 rounded-lg p-4">
          <div className="text-2xl font-bold text-orange-900">{risks.filter(r => r.risk_level === 'high').length}</div>
          <div className="text-sm text-orange-700">High Risks</div>
        </div>
        <div className="bg-yellow-50 border-l-4 border-yellow-600 rounded-lg p-4">
          <div className="text-2xl font-bold text-yellow-900">{risks.filter(r => r.risk_level === 'medium').length}</div>
          <div className="text-sm text-yellow-700">Medium Risks</div>
        </div>
        <div className="bg-green-50 border-l-4 border-green-600 rounded-lg p-4">
          <div className="text-2xl font-bold text-green-900">{risks.filter(r => r.status === 'resolved').length}</div>
          <div className="text-sm text-green-700">Resolved</div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow p-4 flex gap-4 items-center">
        <div className="flex-1">
          <label className="text-xs font-semibold text-slate-600 uppercase mb-1 block">Category</label>
          <select
            value={filterCategory}
            onChange={(e) => setFilterCategory(e.target.value)}
            className="border border-slate-300 rounded px-3 py-2 text-sm w-full"
          >
            <option value="ALL">All Categories</option>
            <option value="Technical">Technical</option>
            <option value="Regulatory">Regulatory</option>
            <option value="Operational">Operational</option>
            <option value="Financial">Financial</option>
          </select>
        </div>
        <div className="flex-1">
          <label className="text-xs font-semibold text-slate-600 uppercase mb-1 block">Risk Level</label>
          <select
            value={filterLevel}
            onChange={(e) => setFilterLevel(e.target.value)}
            className="border border-slate-300 rounded px-3 py-2 text-sm w-full"
          >
            <option value="ALL">All Levels</option>
            <option value="critical">Critical</option>
            <option value="high">High</option>
            <option value="medium">Medium</option>
            <option value="low">Low</option>
          </select>
        </div>
        <div className="text-sm text-slate-600">
          Showing <strong>{filteredRisks.length}</strong> risks
        </div>
      </div>

      {/* Risks List */}
      <div className="space-y-4">
        {filteredRisks.map((risk) => {
          const levelStyle = riskLevelColors[risk.risk_level];
          return (
            <div key={risk.id} className={`bg-white rounded-lg shadow-lg border-l-4 ${levelStyle.border} p-6 hover:shadow-xl transition-shadow`}>
              <div className="flex items-start justify-between mb-4">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <AlertTriangle className="text-slate-400" size={20} />
                    <h3 className="text-lg font-bold text-slate-900">{risk.title}</h3>
                  </div>
                  <p className="text-slate-700 mb-3">{risk.description}</p>
                  <div className="flex items-center gap-4">
                    <span className="text-xs text-slate-500"><strong>Category:</strong> {risk.category}</span>
                    <span className="text-xs text-slate-500"><strong>Owner:</strong> {risk.owner}</span>
                  </div>
                </div>
                <div className="flex flex-col items-end gap-2">
                  <span className={`px-3 py-1 ${levelStyle.bg} ${levelStyle.text} text-xs font-bold rounded-full uppercase`}>
                    {risk.risk_level}
                  </span>
                  <div className="flex items-center gap-2">
                    {statusIcons[risk.status]}
                    <span className="text-sm capitalize">{risk.status}</span>
                  </div>
                </div>
              </div>

              {/* Risk Matrix */}
              <div className="grid grid-cols-3 gap-4 mb-4 p-4 bg-slate-50 rounded-lg">
                <div className="text-center">
                  <div className="text-2xl font-bold text-slate-900">{risk.likelihood}</div>
                  <div className="text-xs text-slate-600">Likelihood</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-slate-900">{risk.impact}</div>
                  <div className="text-xs text-slate-600">Impact</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-red-600">{risk.risk_score}</div>
                  <div className="text-xs text-slate-600">Risk Score</div>
                </div>
              </div>

              {/* Mitigation Strategy */}
              <div className="border-t pt-4">
                <div className="flex items-center gap-2 mb-2">
                  <Shield className="text-blue-600" size={18} />
                  <span className="text-sm font-semibold text-slate-700">Mitigation Strategy</span>
                </div>
                <p className="text-sm text-slate-700 bg-blue-50 p-3 rounded">{risk.mitigation_strategy}</p>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default RiskManagement;